import type { AccessConstraint } from '@sb/shared/access';
import { featureFlag } from '@sb/shared/feature';
import { hasPermission, type PermissionCheck, type PermissionState } from '@sb/shared/permission/util';
import { PlanType, type SubscriptionModel } from '@shiftbase-com/ts-shared/models';
import { hasAtLeastSubscriptionPlan } from '@shiftbase-com/ts-shared/utilities';

/**
 * Access constraints for the payroll routes.
 *
 * The permission list is provisional until SHB-24214 delivers the per-step inventory. It is
 * `any` mode on purpose: a manager needs at least one step they can act on to have anything to
 * do in the flow, and the steps they cannot act on are shown blocked rather than dropped from
 * it. What makes that safe is the department picker below — it only offers departments where the
 * manager holds at least one of these permissions, and the per-step check is any-of across the
 * run's departments, so no scope the header can assemble has every step blocked.
 *
 * That rests on the remaining four steps covering this list one-for-one, none of them plan-gated.
 * The lock step is the deliberate exception: it gates on 'Close timesheets'/'Open timesheets',
 * neither of which admits anyone here, so it can read blocked while the run still has work. An
 * edit to this list has to re-check that — a permission left here that no unlocked step claims
 * admits a manager to a run with nothing to open, and nothing fails loudly when it does.
 *
 * 'Edit minijob balances' is deliberately absent: a module-gated step is dropped from the run
 * rather than shown blocked, so on an account with the module off it would admit a manager to a run
 * whose only step for them does not exist.
 */
export const payrollAccess = {
  payroll: {
    permission: ['View absentee', 'Approve timesheets', 'Edit plus min hours', 'View reports'],
    permissionCheck: 'any',
    // Basic and up: the flow's steps are Basic features (timesheets, absences, plus/min hours,
    // payroll reports) apart from Close Day, which `planLockedPayrollSteps` keeps in the run
    // behind a plan lock for them rather than dropping. Free has none of those blocks and is out
    // of scope (SHB-24639).
    subscriptionPlan: PlanType.BASIC,
    featureFlag: featureFlag.LABS_PAYROLL_PREP,
  },
} satisfies Record<string, AccessConstraint>;

/**
 * Provisional until SHB-24214 delivers a dedicated permission for the payroll-scope picker.
 * Reads the route's own any-of gate rather than restating it (a manager needs at least one of
 * those permissions to reach the flow at all), and scoping the department picker to it keeps
 * departments visible where the manager holds any of them — permissions here are granted per
 * department, not account-wide. Retyping the list here would let SHB-24214's edit to
 * `payrollAccess.payroll.permission` pass this copy by, narrowing the picker back to an
 * empty-options dead end.
 *
 * Shared by the header's department menu and the entry-point filter that decides which of a
 * URL's departments may seed a run, so the two never drift apart on which departments a
 * manager is allowed to see.
 */
export const payrollDepartmentPermissionCheck: PermissionCheck = {
  userId: 'me',
  permissions: payrollAccess.payroll.permission,
  departments: 'any',
};

/**
 * `labsEnabled` is the account-wide "All Labs features" master switch; `featureEnabled` is this
 * flow's own `labs_payroll_prep` flag. A named pair rather than two positional booleans, which a
 * caller can transpose without the compiler noticing.
 */
export interface PayrollLabsAvailability {
  labsEnabled: boolean;
  featureEnabled: boolean;
}

/**
 * Flag + permission only, deliberately without the plan check: an account below the flow's plan
 * still has to see the Payroll entry points, so hiding them on plan here would fail that —
 * {@link isPayrollPlanLocked} drives the separate "show it, but lock it" treatment instead.
 *
 * Shared by the side-nav item and the timesheet actions menu item so the two entry points can't
 * drift onto different gates for the same flow.
 *
 * The master Labs switch is checked here rather than left to the flag alone: turning Labs off has
 * to withdraw every entry point to a Labs feature, whatever the flag's own targeting says.
 */
export function isPayrollVisible(
  { labsEnabled, featureEnabled }: PayrollLabsAvailability,
  permissionState: PermissionState,
): boolean {
  return labsEnabled && featureEnabled && hasPermission(payrollDepartmentPermissionCheck, permissionState);
}

/** Mirrors the plan constraint the route's `accessGuard` enforces, so an entry point can't offer a route it would immediately redirect away from. */
export function isPayrollPlanLocked(subscription: SubscriptionModel | null | undefined): boolean {
  return !hasAtLeastSubscriptionPlan(payrollAccess.payroll.subscriptionPlan, subscription ?? undefined);
}
